import { Component, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, take } from 'rxjs/operators';

import { LanguageCode } from '@language';
import {
    ActionItem,
    ActionItemStyle,
    ApplicationConfigService,
    BaseContainer,
    DisclaimerConfig,
    DisclaimerContent,
    LocalStorageID,
    NavigationLink
} from '@stylehaven/components';
import { addDays } from 'date-fns';

@Component({
    selector: 'med-disclaimer-banner-container',
    templateUrl: './disclaimer-banner-container.component.html'
})
export class DisclaimerBannerContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private disclaimerConfig$: Observable<DisclaimerConfig>;
    private isDisclaimerPageActive$: Observable<boolean>;
    private readonly closedDisclaimer$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly LOCAL_STORAGE_ID: string = LocalStorageID.Disclaimer;
    private readonly DEFAULT_DISCLAIMER_LINK = 'disclaimer';
    private readonly DEFAULT_CONSENT_VALIDITY = 1;
    private readonly DEFAULT_DISCLAIMER: DisclaimerConfig = {
        enabled: false,
        fullContent: {},
        bannerContent: {}
    };

    public showDisclaimer$: Observable<boolean>;
    public disclaimerContent$: Observable<DisclaimerContent | null>;
    public disclaimerLink$: Observable<ActionItem>;
    public acceptButton$: Observable<ActionItem>;

    constructor(
        private readonly configService: ApplicationConfigService,
        private readonly router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.initStateStreams();
        this.initDisclaimerStreams();
        this.initAcceptButton();
        this.initDisclaimerLink();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.closedDisclaimer$.complete();
    }

    private initStateStreams(): void {
        const currentLocation$: Observable<string> = this.router.events.pipe(
            filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.urlAfterRedirects),
            startWith(this.router.url),
            distinctUntilChanged(),
            shareReplay()
        );

        this.isDisclaimerPageActive$ = currentLocation$.pipe(
            map((url: string) => url === `/${this.DEFAULT_DISCLAIMER_LINK}`),
            shareReplay()
        );
    }

    private initDisclaimerStreams(): void {
        this.disclaimerConfig$ = this.configService.getConfig().pipe(
            map(appConfig => appConfig.disclaimerConfig ?? this.DEFAULT_DISCLAIMER),
            take(1)
        );

        this.showDisclaimer$ = combineLatest([
            this.isDisclaimerPageActive$,
            this.disclaimerConfig$,
            this.closedDisclaimer$
        ]).pipe(
            map(
                ([disclaimerPageActive, disclaimerConfig, closed]) =>
                    !disclaimerPageActive && disclaimerConfig.enabled && !closed && !this.hasAcceptedDisclaimer()
            )
        );

        this.disclaimerContent$ = this.disclaimerConfig$.pipe(
            map(disclaimerConfig => this.getDisclaimerBannerContent(disclaimerConfig))
        );
    }

    private hasAcceptedDisclaimer(): boolean {
        const storedExpiryTimeValue = localStorage.getItem(this.LOCAL_STORAGE_ID);

        if (!storedExpiryTimeValue) {
            return false;
        }

        const currentDate: Date = new Date();

        return Number.parseInt(storedExpiryTimeValue) > currentDate.getTime();
    }

    private getDisclaimerBannerContent(disclaimerConfig: DisclaimerConfig): DisclaimerContent | null {
        const currentLang: LanguageCode = this.translateService.getCurrentLanguage();
        const bannerContent: DisclaimerContent | null = disclaimerConfig.bannerContent
            ? disclaimerConfig.bannerContent[currentLang]
            : null;

        if (!bannerContent) {
            console.error('No disclaimer banner translation found');
        }

        return bannerContent;
    }

    private initAcceptButton(): void {
        this.acceptButton$ = this.disclaimerConfig$.pipe(
            map(disclaimerConfig =>
                this.getAcceptButton(disclaimerConfig.consentValidityDays ?? this.DEFAULT_CONSENT_VALIDITY)
            )
        );
    }

    private getAcceptButton(consentValidity: number): ActionItem {
        return {
            label: this.translateService.translate('actions.accept'),
            eventListener: this.closeDisclaimer.bind(this, consentValidity)
        };
    }

    private initDisclaimerLink(): void {
        this.disclaimerLink$ = this.disclaimerConfig$.pipe(
            map(disclaimerConfig => this.getDisclaimerLink(disclaimerConfig))
        );
    }

    private getDisclaimerLink(disclaimerConfig: DisclaimerConfig): ActionItem {
        let navigationLink: NavigationLink;

        if (disclaimerConfig.link) {
            navigationLink = { route: disclaimerConfig.link, target: '_blank' };
        } else {
            navigationLink = { route: this.DEFAULT_DISCLAIMER_LINK, target: '_self' };
        }
        return {
            label: this.translateService.translate('actions.show-more'),
            style: ActionItemStyle.Link,
            navigationLink
        };
    }

    private closeDisclaimer(consentValidity: number): void {
        localStorage.setItem(this.LOCAL_STORAGE_ID, addDays(new Date(), consentValidity).getTime().toString());
        this.closedDisclaimer$.next(true);
    }
}
